import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";

function createId(prefix = "inst") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function createBoardChild(boardDefinitionId, role) {
  const common = {
    id: createId(role),
    definitionId: boardDefinitionId,
    name: `${role} - Board`,
    role,
    parameterOverrides: {},
    children: [],
  };

  if (role === "left") {
    return {
      ...common,
      parameterBindings: {
        width: "parent_boardThickness",
        height: "parent_height",
        depth: "parent_depth",
      },
      transform: {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 90, z: 0 },
      },
    };
  }

  if (role === "right") {
    return {
      ...common,
      parameterBindings: {
        width: "parent_boardThickness",
        height: "parent_height",
        depth: "parent_depth",
      },
      transform: {
        position: {
          x: "parent_width - parent_boardThickness",
          y: 0,
          z: 0,
        },
        rotation: { x: 0, y: 90, z: 0 },
      },
    };
  }

  if (role === "bottom") {
    return {
      ...common,
      parameterBindings: {
        width: "parent_width - parent_boardThickness * 2",
        height: "parent_boardThickness",
        depth: "parent_depth",
      },
      transform: {
        position: {
          x: "parent_boardThickness",
          y: 0,
          z: 0,
        },
        rotation: { x: 90, y: 0, z: 0 },
      },
    };
  }

  if (role === "top") {
    return {
      ...common,
      parameterBindings: {
        width: "parent_width - parent_boardThickness * 2",
        height: "parent_boardThickness",
        depth: "parent_depth",
      },
      transform: {
        position: {
          x: "parent_boardThickness",
          y: "parent_height - parent_boardThickness",
          z: 0,
        },
        rotation: { x: 90, y: 0, z: 0 },
      },
    };
  }

  if (role === "back") {
    return {
      ...common,
      parameterBindings: {
        width: "parent_width - parent_boardThickness * 2",
        height: "parent_height - parent_boardThickness * 2",
        depth: "parent_backThickness",
      },
      transform: {
        position: {
          x: "parent_boardThickness",
          y: "parent_boardThickness",
          z: "parent_depth - parent_backThickness",
        },
        rotation: { x: 0, y: 0, z: 0 },
      },
    };
  }

  return common;
}

export default function InteriorLibraryPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");

  const [showCreateModal, setShowCreateModal] = useState(false);
const [createType, setCreateType] = useState(null);
const [createForm, setCreateForm] = useState({
  name: "",
  code: "",
  description: "",
  category: "",
});
const [categories, setCategories] = useState([]);

  async function load() {
    try {
      setLoading(true);
      const [defsRes, categoriesRes] = await Promise.all([
  interiorLibraryAdminApi.listDefinitions(),
  interiorLibraryAdminApi.listCategories({ scope: "definition" }),
]);

setItems(defsRes.items || []);
setCategories(categoriesRes.items || []);
    } catch (err) {
      console.error("load definitions error:", err);
      alert("Failed to load definitions.");
    } finally {
      setLoading(false);
    }
  }

  async function duplicateDefinition(def) {
  try {
    const payload = {
      code: `${def.code}_copy_${Date.now()}`,
      name: `${def.name || def.code} Copy`,
      node_type: def.node_type || def.nodeType,
      category: def.category,
      description: def.description || "",
      status: "draft",

      default_parameters: def.default_parameters || def.defaultParameters || {},
      geometry_type: def.geometry_type || def.geometryType || "none",
      geometry_defaults: def.geometry_defaults || def.geometryDefaults || {},
      front_face_axis: def.front_face_axis || def.frontFaceAxis || "+z",
      anchors: def.anchors || [],
      children: def.children || [],
      profile: def.profile || null,
      sketch_plane: def.sketch_plane || def.sketchPlane || "xy",
    };

    const res = await interiorLibraryAdminApi.createDefinition(payload);

    const newDef = res.item;

    if (!newDef?.id) {
      await loadDefinitions();
      return;
    }

    if (String(newDef.category || "").startsWith("board.")) {
      navigate(`/interior-editor/definitions/${newDef.id}/board`);
    } else {
      navigate(`/interior-editor/definitions/${newDef.id}/hierarchy`);
    }
  } catch (err) {
    console.error("duplicateDefinition error:", err);
    alert(err?.response?.data?.message || err?.message || "Duplicate failed.");
  }
}

  useEffect(() => {
    load();
  }, []);

  async function createBoardDefinition(form) {
    try {
      const timestamp = Date.now();

      const payload = {
        code: form?.code || `board_panel_${timestamp}`,
name: form?.name || "Board Panel",
category: form?.category || "board.panel",
description: form?.description || "Generic parametric board panel.",
        node_type: "element",
        status: "draft",
        default_parameters: {
          width: 600,
          height: 720,
          depth: 18,
        },
        geometry_type: "profile_extrude",
        geometry_defaults: {
          thickness: "depth",
        },
        front_face_axis: "+z",
        anchors: [],
        children: [],
        profile: {
          type: "rect",
          width_expr: "width",
          height_expr: "height",
        },
        sketch_plane: "xy",
      };

      const res = await interiorLibraryAdminApi.createDefinition(payload);
      const id = res?.item?.id;

      if (id) {
        navigate(`/interior-editor/definitions/${id}/board`);
      } else {
        await load();
      }
    } catch (err) {
      console.error("create board definition error:", err);
      alert(err?.response?.data?.message || "Failed to create board.");
    }
  }

  async function createDoorFrontDefinition(form) {
  try {
    const timestamp = Date.now();

    const boardDef = items.find(
      (x) =>
        x.node_type === "element" &&
        String(x.category || "") === "board.panel"
    );

    if (!boardDef) {
      alert("Create Board Panel definition first.");
      return;
    }

    const payload = {
      code: form?.code || `front_door_${timestamp}`,
name: form?.name || "Door Front",
category: form?.category || "front.door",
description:
  form?.description ||
  "Front door element based on a board panel, prepared for handles, hinges and processing.",
      node_type: "element",
      status: "draft",
      default_parameters: {
  width: 600,
  height: 720,
  depth: 18,

  hingeDefinitionId: "",
  handleDefinitionId: "",

  hingeCount: 2,
  hingeSide: "left",
  hingeTopOffset: 100,
  hingeBottomOffset: 100,

  handleEnabled: 1,
  handleSide: "opposite_hinge",
  handleOffsetX: 40,
  handleOffsetY: 0,
  handleOffsetZ: 10,
},
      geometry_type: "none",
geometry_defaults: {},
children: [
  {
    id: createId("front_board"),
    definitionId: boardDef.id,
    name: "Door board",
    role: "front_board",
    placement: {
      mode: "snap",
      face: "front",
    },
    parameterBindings: {
      width: "parent_width",
      height: "parent_height",
      depth: "parent_depth",
    },
    parameterOverrides: {},
    children: [],
  },
],
profile: null,
sketch_plane: "xy",
      metadata: {
        visualRole: "front",
        baseDefinitionId: boardDef.id,
      },
    };

    const res = await interiorLibraryAdminApi.createDefinition(payload);
    const id = res?.item?.id;

    if (id) {
      navigate(`/interior-editor/definitions/${id}/hierarchy`);
    } else {
      await load();
    }
  } catch (err) {
    console.error("create door front definition error:", err);
    alert(err?.response?.data?.message || "Failed to create Door Front.");
  }
}

  async function createCabinetBoxDefinition(form) {
  try {
    const timestamp = Date.now();

    let boardDef = items.find(
  (x) =>
    x.node_type === "element" &&
    String(x.category || "") === "board.panel"
);

if (!boardDef) {
  alert("Create Board Panel definition first.");
  return;
}

const createSnapChild = (role, face) => {
  const parentTrimLeft = `parent_${role}TrimLeft`;
  const parentTrimRight = `parent_${role}TrimRight`;
  const parentTrimTop = `parent_${role}TrimTop`;
  const parentTrimBottom = `parent_${role}TrimBottom`;

  const parentOffsetX = `parent_${role}OffsetX`;
  const parentOffsetY = `parent_${role}OffsetY`;
  const parentOffsetZ = `parent_${role}OffsetZ`;

  let bindings = {
    trimLeft: parentTrimLeft,
    trimRight: parentTrimRight,
    trimTop: parentTrimTop,
    trimBottom: parentTrimBottom,

    offsetX: parentOffsetX,
    offsetY: parentOffsetY,
    offsetZ: parentOffsetZ,
  };

  if (role === "left" || role === "right") {
  bindings = {
    ...bindings,
    width: `parent_depth - ${parentTrimLeft} - ${parentTrimRight}`,
    height: `parent_height - ${parentTrimTop} - ${parentTrimBottom}`,
    depth: "parent_boardThickness",
  };
}

if (role === "top" || role === "bottom") {
  bindings = {
    ...bindings,
    width: `parent_width - ${parentTrimLeft} - ${parentTrimRight}`,
    height: `parent_depth - ${parentTrimTop} - ${parentTrimBottom}`,
    depth: "parent_boardThickness",
  };
}

if (role === "back") {
  bindings = {
    ...bindings,
    width: `parent_width - ${parentTrimLeft} - ${parentTrimRight}`,
    height: `parent_height - ${parentTrimTop} - ${parentTrimBottom}`,
    depth: "parent_backThickness",
  };
}

if (role === "front") {
  bindings = {
    ...bindings,
    width: `parent_width - ${parentTrimLeft} - ${parentTrimRight}`,
    height: `parent_height - ${parentTrimTop} - ${parentTrimBottom}`,
    depth: "parent_boardThickness",
  };
}

  return {
    id: createId(role),
    definitionId: boardDef.id,
    name: `${role} board`,
    role,
    placement: {
      mode: "snap",
      face,
    },
    parameterBindings: bindings,
    parameterOverrides: {},
    children: [],
  };
};

    const children = [
      createSnapChild("left", "left"),
      createSnapChild("right", "right"),
      createSnapChild("top", "top"),
      createSnapChild("bottom", "bottom"),
      createSnapChild("back", "back"),
      createSnapChild("front", "front"),
    ];

    const payload = {
      code: form.code,
name: form.name,
description: form.description,
category: form.category || "cabinet.box",
      node_type: "module",
      status: "draft",

      default_parameters: {
  width: 600,
  height: 720,
  depth: 560,

  boardThickness: 18,
  backThickness: 8,

  frontGapOuter: 2,
frontGapInner: 2,

leftOffsetX: 0,
leftOffsetY: 0,
leftOffsetZ: 0,
rightOffsetX: 0,
rightOffsetY: 0,
rightOffsetZ: 0,
topOffsetX: 0,
topOffsetY: 0,
topOffsetZ: 0,
bottomOffsetX: 0,
bottomOffsetY: 0,
bottomOffsetZ: 0,
backOffsetX: 0,
backOffsetY: 0,
backOffsetZ: 0,
frontOffsetX: 0,
frontOffsetY: 0,
frontOffsetZ: 0,

  // FRONT
  frontTrimTop: 0,
  frontTrimBottom: 0,
  frontTrimLeft: 0,
  frontTrimRight: 0,

  // BACK
  backTrimLeft: "boardThickness / 2",
  backTrimRight: "boardThickness / 2",
  backTrimTop: "boardThickness / 2",
  backTrimBottom: "boardThickness / 2",

  // LEFT
  leftTrimTop: 0,
  leftTrimBottom: 0,
  leftTrimLeft: 0,
  leftTrimRight: "boardThickness",

  // RIGHT
  rightTrimTop: 0,
  rightTrimBottom: 0,
  rightTrimLeft: "boardThickness",
  rightTrimRight: 0,

  // TOP
  topTrimTop: "boardThickness",
  topTrimBottom: 0,
  topTrimLeft: "boardThickness",
  topTrimRight: "boardThickness",

  // BOTTOM
  bottomTrimTop: "boardThickness",
  bottomTrimBottom: 0,
  bottomTrimLeft: "boardThickness",
  bottomTrimRight: "boardThickness",

  constructionMode: "between_sides",

        floorOffset: 0,

shelfCount: 0,
shelfThickness: "boardThickness",

shelfTrimLeft: "boardThickness",
shelfTrimRight: "boardThickness",
shelfTrimTop: "boardThickness",
shelfTrimBottom: "boardThickness",
shelfOffsetX: 0,
shelfOffsetY: 0,
shelfOffsetZ: 0,
      },

      constraints: {
  innerHeight: "height - boardThickness * 2",
  shelfSpacing: "innerHeight / (shelfCount + 1)",
},

      // 🔥 KLJUČNO: imamo box kao gabarit
      geometry_type: "box",
      geometry_defaults: {
        width: "width",
        height: "height",
        depth: "depth",
      },

      front_face_axis: "+z",
      anchors: [],
      children,

      partitions: {
  front: {
    horizontal: [],
    vertical: [],
  },
},

      metadata: {
        visualRole: "container",
        transparent: true,
      },
    };

    const res = await interiorLibraryAdminApi.createDefinition(payload);
    const id = res?.item?.id;

    if (id) {
      navigate(`/interior-editor/definitions/${id}/hierarchy`);
    } else {
      await load();
    }
  } catch (err) {
    console.error("create cabinet box error:", err);
    alert(err?.response?.data?.message || "Failed to create cabinet.");
  }
}

  async function createTestBoxDefinition() {
  try {
    const timestamp = Date.now();

    const payload = {
      code: `primitive_box_${timestamp}`,
      name: "Test Box",
      node_type: "element",
      category: "primitive.box",
      description: "Simple editable box for project scene testing.",
      status: "draft",
      default_parameters: {
        width: 600,
        height: 720,
        depth: 560,
      },
      geometry_type: "box",
      geometry_defaults: {
        width: "width",
        height: "height",
        depth: "depth",
      },
      front_face_axis: "+z",
      anchors: [],
      children: [],
    };

    const res = await interiorLibraryAdminApi.createDefinition(payload);
    const id = res?.item?.id;

    if (id) {
      navigate(`/interior-editor/definitions/${id}/hierarchy`);
    } else {
      await load();
    }
  } catch (err) {
    console.error("create test box error:", err);
    alert(err?.response?.data?.message || "Failed to create Test Box.");
  }
}

  async function createDefinition(nodeType) {
    try {
      const timestamp = Date.now();

      let payload = null;

      if (nodeType === "module") {
        payload = {
          code: `module_${timestamp}`,
          name: "New Module Definition",
          node_type: "module",
          category: "module.generic",
          description: "",
          status: "draft",
          default_parameters: {
            width: 600,
            height: 720,
            depth: 560,
            boardThickness: "project_boardThickness",
            backThickness: "project_backThickness",
          },
          geometry_type: "none",
          geometry_defaults: {},
          front_face_axis: "+z",
          anchors: [],
          children: [],
        };
      }

      if (nodeType === "element") {
        payload = {
          code: `element_${timestamp}`,
          name: "New Element Definition",
          node_type: "element",
          category: "element.generic",
          description: "",
          status: "draft",
          default_parameters: {
            width: 100,
            height: 100,
            depth: 18,
          },
          geometry_type: "box",
          geometry_defaults: {
            width: "width",
            height: "height",
            depth: "depth",
          },
          front_face_axis: "+z",
          anchors: [],
          children: [],
        };
      }

      if (nodeType === "hardware") {
  payload = {
    code: `hardware_${timestamp}`,
    name: "New Hardware Definition",
    node_type: "hardware",
    category: "hardware.generic",
    description: "",
    status: "draft",
    default_parameters: {
      width: 10,
      height: 10,
      depth: 10,

      hardwareKind: "generic",
      handedness: "universal",
      mirrorAllowed: 1,

      mountOffsetX: 0,
      mountOffsetY: 0,
      mountOffsetZ: 0,

      mountRotationX: 0,
      mountRotationY: 0,
      mountRotationZ: 0,
    },
    geometry_type: "catalog_glb",
    geometry_defaults: {},
    front_face_axis: "+z",
    anchors: [],
    children: [],
  };
}

      if (!payload) return;

      const res = await interiorLibraryAdminApi.createDefinition(payload);
      const id = res?.item?.id;

      if (id) {
        navigate(`/interior-editor/definitions/${id}/hierarchy`);
      } else {
        await load();
      }
    } catch (err) {
      console.error("create definition error:", err);
      alert(err?.response?.data?.message || "Failed to create definition.");
    }
  }

  async function handleDelete(item) {
    const confirmed = window.confirm(`Delete definition "${item.name}"?`);
    if (!confirmed) return;

    try {
      await interiorLibraryAdminApi.deleteDefinition(item.id);
      await load();
    } catch (err) {
      console.error("delete definition error:", err);
      alert(err?.response?.data?.message || "Failed to delete definition.");
    }
  }

  const filteredItems = useMemo(() => {
    if (filterType === "all") return items;
    return items.filter((item) => item.node_type === filterType);
  }, [items, filterType]);

  const grouped = useMemo(() => {
  return {
    module: filteredItems.filter((item) => item.node_type === "module"),

    board: filteredItems.filter(
      (item) =>
        item.node_type === "element" &&
        String(item.category || "").startsWith("board.")
    ),

    front: filteredItems.filter(
      (item) =>
        item.node_type === "element" &&
        String(item.category || "").startsWith("front.")
    ),

    element: filteredItems.filter(
      (item) =>
        item.node_type === "element" &&
        !String(item.category || "").startsWith("board.") &&
        !String(item.category || "").startsWith("front.")
    ),

    hardware: filteredItems.filter((item) => item.node_type === "hardware"),
    project: filteredItems.filter((item) => item.node_type === "project"),
  };
}, [filteredItems]);

  function openDefinition(item) {
    const isBoardLike =
  item.node_type === "element" &&
  String(item.category || "").startsWith("board.");

if (isBoardLike) {
      navigate(`/interior-editor/definitions/${item.id}/board`);
    } else {
      navigate(`/interior-editor/definitions/${item.id}/hierarchy`);
    }
  }

  function renderGroup(title, rows) {
    if (!rows.length) return null;

    return (
      <div className="border rounded overflow-hidden bg-white">
        <div className="px-3 py-2 bg-gray-100 font-semibold">{title}</div>

        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((item) => (
              <tr key={item.id} className="border-t align-top">
                <td className="px-3 py-2">
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-gray-500">id: {item.id}</div>
                </td>

                <td className="px-3 py-2">{item.code}</td>
                <td className="px-3 py-2">{item.category || "—"}</td>
                <td className="px-3 py-2">{item.status}</td>

                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => openDefinition(item)}
                      className="px-3 py-1 rounded border"
                    >
                      Open / Edit
                    </button>

                    <button
  type="button"
  onClick={() => duplicateDefinition(item)}
  className="px-3 py-1.5 rounded border bg-white text-blue-600"
>
  Duplicate
</button>

                    <button
                      onClick={() => handleDelete(item)}
                      className="px-3 py-1 rounded border text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 bg-gray-50 min-h-[calc(100vh-80px)]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Interior Library</h1>
          <div className="text-sm text-gray-500">
            Definitions for modules, boards, hardware and reusable parametric parts.
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
         <button
  onClick={() => {
    setCreateType("board");
    const defaultCategory =
  categories.find((c) => c.category_key === "board.panel")?.category_key ||
  categories[0]?.category_key ||
  "";

setCreateForm({
  name: "Board Panel",
  code: `board_panel_${Date.now()}`,
  description: "Generic parametric board panel.",
  category: defaultCategory,
});
    setShowCreateModal(true);
  }}
  className="px-4 py-2 rounded bg-blue-600 text-white"
>
  + Board Panel
</button>

<button
  onClick={() => {
  setCreateType("door");

  const defaultCategory =
    categories.find((c) => c.category_key === "front.door")?.category_key ||
    categories[0]?.category_key ||
    "";

  setCreateForm({
    name: "Door Front",
    code: `front_door_${Date.now()}`,
    description:
      "Front door element based on a board panel, prepared for handles, hinges and processing.",
    category: defaultCategory,
  });

  setShowCreateModal(true);
}}
  className="px-4 py-2 rounded bg-amber-600 text-white"
>
  + Door Front
</button>

<button
  onClick={() => {
  setCreateType("cabinet");

  const defaultCategory =
    categories.find((c) => c.category_key === "cabinet.box")?.category_key ||
    categories[0]?.category_key ||
    "";

  setCreateForm({
    name: "Cabinet Box",
    code: `cabinet_box_${Date.now()}`,
    description: "",
    category: defaultCategory,
  });

  setShowCreateModal(true);
}}
  className="px-4 py-2 rounded bg-purple-600 text-white"
>
  + Cabinet Box
</button>

          <button
            onClick={() => createDefinition("module")}
            className="px-4 py-2 rounded border bg-white"
          >
            + Module
          </button>

          <button
            onClick={() => createDefinition("hardware")}
            className="px-4 py-2 rounded border bg-white"
          >
            + Hardware
          </button>

          <button
            onClick={() => navigate("/interior-editor/projects")}
            className="px-4 py-2 rounded border bg-white"
          >
            Projects
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm font-medium">Filter</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border rounded bg-white"
        >
          <option value="all">All</option>
          <option value="module">Modules</option>
          <option value="element">Elements</option>
          <option value="hardware">Hardware</option>
          <option value="project">Projects</option>
        </select>

        <button onClick={load} className="px-4 py-2 rounded border bg-white">
          Refresh
        </button>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="space-y-4">
          {renderGroup("Cabinet / Modules", grouped.module)}
{renderGroup("Front Elements", grouped.front)}
{renderGroup("Other Elements", grouped.element)}
{renderGroup("Hardware / Processing", grouped.hardware)}
{renderGroup("Board Panels", grouped.board)}
{renderGroup("Project Templates", grouped.project)}

          {!filteredItems.length && (
            <div className="border rounded p-4 text-gray-500 bg-white">
              No definitions found.
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
    <div className="bg-white p-4 rounded w-[400px] space-y-3">
      <div className="font-semibold">Create Definition</div>

      <input
        value={createForm.name}
        onChange={(e) =>
          setCreateForm((p) => ({ ...p, name: e.target.value }))
        }
        placeholder="Name"
        className="w-full px-2 py-1 border rounded"
      />

      <input
        value={createForm.code}
        onChange={(e) =>
          setCreateForm((p) => ({ ...p, code: e.target.value }))
        }
        placeholder="Code"
        className="w-full px-2 py-1 border rounded"
      />

      <input
        value={createForm.description}
        onChange={(e) =>
          setCreateForm((p) => ({ ...p, description: e.target.value }))
        }
        placeholder="Description"
        className="w-full px-2 py-1 border rounded"
      />

      <select
  value={createForm.category || ""}
  onChange={(e) =>
    setCreateForm((p) => ({ ...p, category: e.target.value }))
  }
  className="w-full px-2 py-1 border rounded bg-white"
>
  <option value="">Select category...</option>

  {categories.map((c) => (
    <option key={c.id} value={c.category_key}>
      {c.label}
    </option>
  ))}
</select>

      <div className="flex gap-2 justify-end">
        <button
          onClick={() => setShowCreateModal(false)}
          className="px-3 py-1 border rounded"
        >
          Cancel
        </button>

        <button
          onClick={async () => {
            if (createType === "cabinet") {
              await createCabinetBoxDefinition(createForm);
            }

            if (createType === "door") {
              await createDoorFrontDefinition(createForm);
            }

            if (createType === "board") {
              await createBoardDefinition(createForm);
            }

            setShowCreateModal(false);
          }}
          className="px-3 py-1 bg-blue-600 text-white rounded"
        >
          Create
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}